import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
    Wallet, Plus, Minus, Delete, X, Play, Lock, FileSpreadsheet,
    History as HistoryIcon, AlertTriangle, CheckCircle2, RotateCcw, Loader2,
    HelpCircle,
} from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError, useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import GuideTour from "@/components/GuideTour";

const NZ_MONEY = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 2 });

const KEYPAD = [
    ["7", "8", "9"],
    ["4", "5", "6"],
    ["1", "2", "3"],
    [".", "0", "back"],
];

// Steps for the first-run guided tour. Each target matches a `data-tour-id`
// on an element inside the Cash Register. The tour auto-opens on first
// visit; users can re-open it anytime from the `?` button in the header.
const CASH_REGISTER_TOUR_STEPS = [
    {
        target: "balance",
        title: "Welcome to your Cash Register",
        body: "This is where you track every dollar of cash that comes in and out. Your live balance is right here — updated with every entry.",
        placement: "bottom",
    },
    {
        target: "opening-float",
        title: "Start with your opening float",
        body: "Kick off each day by recording the cash you start with in the till. This becomes your first ledger entry so the numbers add up.",
        placement: "bottom",
    },
    {
        target: "category",
        title: "Pick a category",
        body: "Every entry needs a category (e.g. Cash sale, Purchase, Drawings). Your accountant uses these to code your ledger correctly.",
        placement: "bottom",
    },
    {
        target: "keypad",
        title: "Type the amount",
        body: "Tap the on-screen keypad to enter the amount. Big buttons so it's fast even on a busy counter.",
        placement: "top",
    },
    {
        target: "record-buttons",
        title: "Cash In or Cash Out",
        body: "Green for money coming in, red for money going out. One tap posts the entry and updates your balance instantly.",
        placement: "top",
    },
    {
        target: "day-close",
        title: "End your day with a count",
        body: "At close, physically count the till and enter it here. We'll flag any variance so you can note what happened.",
        placement: "bottom",
    },
    {
        target: "export",
        title: "Share with your accountant",
        body: "Export the whole ledger to Excel — matched to your accountant's layout — anytime you need to reconcile or file.",
        placement: "bottom",
    },
    {
        target: "entries",
        title: "Every entry is auditable",
        body: "Your full history lives here. Made a mistake? Tap 'Adjust' on any row to post a correction — the original stays on record.",
        placement: "top",
    },
];

// -------------------------------------------------------------------------
export default function CashRegister() {
    const { activeClient } = useAuth();
    const clientId = activeClient?.id;

    const [status, setStatus] = useState(null);
    const [entries, setEntries] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [amount, setAmount] = useState("");
    const [description, setDescription] = useState("");
    const [category, setCategory] = useState("cash_sale");
    const [reference, setReference] = useState("");
    const [busy, setBusy] = useState(false);
    const [openDlg, setOpenDlg] = useState(null); // 'opening' | 'close' | 'adjust' | 'export'
    const [adjustSource, setAdjustSource] = useState(null);
    const tourRef = useRef(null);

    async function refresh() {
        if (!clientId) return;
        setLoading(true);
        try {
            const [s, e, c] = await Promise.all([
                api.get("/cash-register/status", { params: { client_id: clientId } }),
                api.get("/cash-register/entries", { params: { client_id: clientId, limit: 100 } }),
                api.get("/cash-register/categories"),
            ]);
            setStatus(s.data);
            setEntries(e.data);
            setCategories(c.data);
        } catch (err) {
            const msg = formatApiError(err);
            toast.error(msg);
            setStatus({ enabled: false, error: msg });
        } finally { setLoading(false); }
    }
    useEffect(() => { refresh(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

    const balance = status?.balance || 0;
    const hasOpening = !!status?.opening_float_today;

    function pressKey(k) {
        if (k === "back") { setAmount((a) => a.slice(0, -1)); return; }
        if (k === ".") {
            if (!amount.includes(".")) setAmount((a) => (a === "" ? "0." : a + "."));
            return;
        }
        // Prevent numbers > 8 digits, decimals limited to 2
        if (amount.includes(".")) {
            const [, dec] = amount.split(".");
            if (dec.length >= 2) return;
        } else if (amount.length >= 8) return;
        setAmount((a) => (a === "0" ? k : a + k));
    }
    function clearForm() {
        setAmount("");
        setDescription("");
        setReference("");
        setCategory("cash_sale");
    }

    const parsedAmount = useMemo(() => {
        const v = parseFloat(amount);
        return Number.isFinite(v) && v > 0 ? Math.round(v * 100) / 100 : 0;
    }, [amount]);

    const activeCat = useMemo(() => categories.find((c) => c.key === category) || null, [categories, category]);

    async function submitEntry(direction) {
        if (!parsedAmount) { toast.error("Enter an amount first"); return; }
        if (!description.trim()) { toast.error("Add a short description"); return; }
        if (activeCat && activeCat.direction !== "both" && activeCat.direction !== direction) {
            toast.error(`"${activeCat.label}" is a ${activeCat.direction === "cash_in" ? "Cash In" : "Cash Out"} category`);
            return;
        }
        setBusy(true);
        try {
            await api.post("/cash-register/entries", {
                client_id: clientId,
                direction,
                amount: parsedAmount,
                description: description.trim(),
                category,
                reference: reference.trim() || null,
            });
            toast.success(`${direction === "cash_in" ? "Cash In" : "Cash Out"} ${NZ_MONEY.format(parsedAmount)} recorded`);
            clearForm();
            refresh();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    }

    if (!clientId) {
        return <div className="text-sm text-[#5E636E]">Pick an entity from the top-right to open its cash register.</div>;
    }

    if (loading && !status) {
        return <div className="text-sm text-[#5E636E]"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading register…</div>;
    }

    if (status && status.enabled === false) {
        return (
            <div className="co-card co-card-pad text-center" data-testid="cash-register-disabled">
                <Wallet className="mx-auto h-8 w-8 text-[#5E636E]" />
                <div className="mt-3 text-base font-medium">Cash register isn&apos;t enabled for this business</div>
                <div className="mt-1.5 text-sm text-[#5E636E]">Ask your accountant to switch on the Cash on Hand Register add-on for {activeClient?.name || "this entity"}.</div>
            </div>
        );
    }

    return (
        <div data-testid="cash-register-page" className="space-y-4">
            {/* Balance header */}
            <div className="co-card co-card-pad" data-testid="cash-balance-card" data-tour-id="balance">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Cash on hand</div>
                        <div className={`mt-1 text-4xl sm:text-5xl font-display font-semibold tracking-tight ${balance < 0 ? "text-rose-700" : "text-[#0F1115]"}`} data-testid="cash-balance">
                            {NZ_MONEY.format(balance)}
                        </div>
                        <div className="mt-1 text-xs text-[#5E636E]">
                            {status?.entries_count || 0} entries · {hasOpening ? "Opening float set today" : "No opening float today"}
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                        {!hasOpening && (
                            <Button size="sm" variant="outline" onClick={() => setOpenDlg("opening")} data-testid="cash-open-float-btn" data-tour-id="opening-float">
                                <Play className="h-3.5 w-3.5" /> Opening float
                            </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => setOpenDlg("close")} data-testid="cash-day-close-btn" data-tour-id="day-close">
                            <Lock className="h-3.5 w-3.5" /> End-of-day count
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setOpenDlg("export")} data-testid="cash-export-btn" data-tour-id="export">
                            <FileSpreadsheet className="h-3.5 w-3.5" /> Export
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => tourRef.current?.open()} data-testid="cash-guide-btn" aria-label="Show me around">
                            <HelpCircle className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Calculator — form first (What + Description), then keypad (How much + record) */}
            <div className="grid md:grid-cols-[minmax(0,320px)_minmax(0,1fr)] gap-4">
                <div className="co-card co-card-pad space-y-3" data-testid="cash-form-card" data-tour-id="form-card">
                    <div data-tour-id="category">
                        <Label className="co-label">1 · Category</Label>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger data-testid="cash-category-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {categories.map((c) => (
                                    <SelectItem key={c.key} value={c.key}>{c.label} <span className="text-[10px] text-[#5E636E] ml-1">({c.prefix})</span></SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {activeCat && (
                            <div className="mt-1 text-[10px] text-[#5E636E]">
                                {activeCat.direction === "cash_in" ? "Money coming in" : activeCat.direction === "cash_out" ? "Money going out" : "Either direction"}
                            </div>
                        )}
                    </div>
                    <div>
                        <Label className="co-label">2 · Description</Label>
                        <Input
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="e.g. Sunday market takings"
                            maxLength={200}
                            data-testid="cash-description-input"
                        />
                    </div>
                    <div>
                        <Label className="co-label">Reference <span className="text-[10px] text-[#5E636E]">(auto if blank)</span></Label>
                        <Input
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            placeholder="e.g. S042"
                            maxLength={30}
                            data-testid="cash-reference-input"
                        />
                    </div>
                </div>

                <div className="co-card co-card-pad" data-testid="cash-keypad-card" data-tour-id="keypad">
                    <div className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">3 · Amount</div>
                    <div className="mt-1 flex items-baseline gap-2">
                        <div className="text-4xl sm:text-5xl font-display font-medium tabular-nums" data-testid="cash-amount-display">
                            {amount || "0"}
                        </div>
                        {amount && (
                            <button onClick={() => setAmount("")} className="ml-auto rounded-full border border-[#E4E4E7] px-2 py-0.5 text-[10px] text-[#5E636E] hover:bg-zinc-50" data-testid="cash-amount-clear">
                                clear
                            </button>
                        )}
                    </div>

                    <div className="mt-3 grid grid-cols-3 gap-2" data-testid="cash-keypad">
                        {KEYPAD.flat().map((k) => (
                            <button
                                key={k}
                                type="button"
                                onClick={() => pressKey(k)}
                                className="h-14 sm:h-16 rounded-lg border border-[#E4E4E7] bg-white hover:bg-zinc-50 active:bg-zinc-100 transition-colors text-xl font-medium tabular-nums flex items-center justify-center select-none"
                                data-testid={`cash-key-${k}`}
                            >
                                {k === "back" ? <Delete className="h-5 w-5" /> : k}
                            </button>
                        ))}
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2" data-tour-id="record-buttons">
                        <Button
                            className="h-14 bg-emerald-600 hover:bg-emerald-700 text-white text-base font-semibold"
                            onClick={() => submitEntry("cash_in")}
                            disabled={busy || !parsedAmount || !description.trim()}
                            data-testid="cash-in-btn"
                        >
                            <Plus className="h-5 w-5" /> Cash In
                        </Button>
                        <Button
                            className="h-14 bg-rose-600 hover:bg-rose-700 text-white text-base font-semibold"
                            onClick={() => submitEntry("cash_out")}
                            disabled={busy || !parsedAmount || !description.trim()}
                            data-testid="cash-out-btn"
                        >
                            <Minus className="h-5 w-5" /> Cash Out
                        </Button>
                    </div>
                </div>
            </div>

            {/* Recent entries */}
            <div className="co-card" data-testid="cash-entries-card" data-tour-id="entries">
                <div className="flex items-center justify-between px-4 py-3 border-b border-[#E4E4E7]">
                    <div className="flex items-center gap-2">
                        <HistoryIcon className="h-4 w-4 text-[#5E636E]" />
                        <span className="text-sm font-medium">Recent entries</span>
                        <span className="text-[10px] text-[#5E636E]">({entries.length})</span>
                    </div>
                </div>
                {entries.length === 0 ? (
                    <div className="p-8 text-center text-sm text-[#5E636E]">No entries yet — set your opening float to start.</div>
                ) : (
                    <ul className="divide-y divide-[#E4E4E7]" data-testid="cash-entries-list">
                        {entries.map((e) => (
                            <EntryRow key={e.id} entry={e} onAdjust={() => { setAdjustSource(e); setOpenDlg("adjust"); }} />
                        ))}
                    </ul>
                )}
            </div>

            {/* Dialogs */}
            {openDlg === "opening" && (
                <OpeningFloatDialog
                    clientId={clientId}
                    onDone={() => { setOpenDlg(null); refresh(); }}
                    onClose={() => setOpenDlg(null)}
                />
            )}
            {openDlg === "close" && (
                <DayCloseDialog
                    clientId={clientId}
                    expected={balance}
                    onDone={() => { setOpenDlg(null); refresh(); }}
                    onClose={() => setOpenDlg(null)}
                />
            )}
            {openDlg === "adjust" && adjustSource && (
                <AdjustmentDialog
                    source={adjustSource}
                    onDone={() => { setOpenDlg(null); setAdjustSource(null); refresh(); }}
                    onClose={() => { setOpenDlg(null); setAdjustSource(null); }}
                />
            )}
            {openDlg === "export" && (
                <ExportDialog
                    clientId={clientId}
                    onClose={() => setOpenDlg(null)}
                />
            )}

            {/* Step-by-step guided tour — auto-opens on first visit, reopen
                anytime via the ? button in the header. */}
            <GuideTour
                ref={tourRef}
                storageKey="co.cashRegisterGuide.v1"
                steps={CASH_REGISTER_TOUR_STEPS}
            />
        </div>
    );
}


function EntryRow({ entry, onAdjust }) {
    const isIn = (entry.cash_in || 0) > 0;
    const amt = entry.cash_in || entry.cash_out || 0;
    const isAdj = entry.kind === "adjustment";
    return (
        <li className="px-4 py-3 flex items-center gap-3" data-testid={`cash-entry-${entry.id}`}>
            <div className={`grid h-8 w-8 place-items-center rounded-full ${isAdj ? "bg-amber-50 text-amber-700" : isIn ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                {isAdj ? <RotateCcw className="h-4 w-4" /> : isIn ? <Plus className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[11px] text-[#5E636E]">{entry.reference}</span>
                    <span className="text-sm truncate">{entry.description}</span>
                    {isAdj && <span className="rounded-full bg-amber-100 text-amber-800 text-[9px] px-1.5 py-0.5 uppercase tracking-wide">Adjustment</span>}
                </div>
                <div className="text-[11px] text-[#5E636E]">
                    {new Date(entry.occurred_at).toLocaleString("en-NZ")} · {entry.entered_by_name}
                </div>
            </div>
            <div className="text-right shrink-0">
                <div className={`text-sm font-semibold tabular-nums ${isIn ? "text-emerald-700" : "text-rose-700"}`}>
                    {isIn ? "+" : "−"}{NZ_MONEY.format(amt)}
                </div>
                <div className="text-[10px] text-[#5E636E] tabular-nums">bal {NZ_MONEY.format(entry.running_balance)}</div>
            </div>
            {!isAdj && (
                <button onClick={onAdjust} className="ml-1 text-[10px] text-[#5E636E] hover:text-[#0F1115] underline decoration-dotted underline-offset-2" data-testid={`cash-entry-adjust-${entry.id}`}>
                    Adjust
                </button>
            )}
        </li>
    );
}


function OpeningFloatDialog({ clientId, onDone, onClose }) {
    const [amount, setAmount] = useState("");
    const [busy, setBusy] = useState(false);
    async function submit() {
        const v = parseFloat(amount);
        if (!(v > 0)) { toast.error("Amount must be greater than zero"); return; }
        setBusy(true);
        try {
            await api.post("/cash-register/opening-float", { client_id: clientId, amount: v });
            toast.success(`Opening float ${NZ_MONEY.format(v)} recorded`);
            onDone();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    }
    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="cash-opening-dialog">
                <DialogHeader>
                    <DialogTitle>Set opening float</DialogTitle>
                    <DialogDescription>The starting cash in the till for today. This will be your first ledger entry.</DialogDescription>
                </DialogHeader>
                <div>
                    <Label className="co-label">Opening cash amount (NZD)</Label>
                    <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus data-testid="cash-opening-amount" />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white" onClick={submit} disabled={busy} data-testid="cash-opening-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Set opening float
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function DayCloseDialog({ clientId, expected, onDone, onClose }) {
    const [counted, setCounted] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const cv = parseFloat(counted);
    const variance = Number.isFinite(cv) ? Math.round((cv - expected) * 100) / 100 : null;
    const hasVariance = variance !== null && Math.abs(variance) > 0.005;

    async function submit() {
        if (!Number.isFinite(cv) || cv < 0) { toast.error("Enter the physical cash counted"); return; }
        if (hasVariance && !note.trim()) { toast.error("Explain the variance to close out the day"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/cash-register/day-close", {
                client_id: clientId,
                counted_balance: cv,
                variance_note: note.trim() || null,
            });
            toast.success(`Day closed · variance ${NZ_MONEY.format(data.variance)}`);
            onDone();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    }
    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="cash-close-dialog">
                <DialogHeader>
                    <DialogTitle>End-of-day cash count</DialogTitle>
                    <DialogDescription>Count what&apos;s physically in the till and enter it below. We&apos;ll compare against the running balance and flag any variance.</DialogDescription>
                </DialogHeader>
                <div className="rounded-md border border-[#E4E4E7] bg-[#FAFAFA] p-3 text-sm flex items-center justify-between">
                    <span className="text-[#5E636E]">Expected balance</span>
                    <span className="font-semibold tabular-nums">{NZ_MONEY.format(expected)}</span>
                </div>
                <div>
                    <Label className="co-label">Counted cash (NZD)</Label>
                    <Input type="number" step="0.01" value={counted} onChange={(e) => setCounted(e.target.value)} autoFocus data-testid="cash-close-counted" />
                </div>
                {variance !== null && (
                    <div className={`rounded-md p-2 text-xs ${hasVariance ? (variance < 0 ? "bg-rose-50 text-rose-800 border border-rose-200" : "bg-amber-50 text-amber-800 border border-amber-200") : "bg-emerald-50 text-emerald-800 border border-emerald-200"}`} data-testid="cash-close-variance">
                        {hasVariance ? (
                            <><AlertTriangle className="inline h-3.5 w-3.5 mr-1" />
                                Variance {variance < 0 ? "short" : "over"} by <strong>{NZ_MONEY.format(Math.abs(variance))}</strong> — please add a note.</>
                        ) : (
                            <><CheckCircle2 className="inline h-3.5 w-3.5 mr-1" /> Counted matches expected — no variance.</>
                        )}
                    </div>
                )}
                {hasVariance && (
                    <div>
                        <Label className="co-label">Variance reason (required)</Label>
                        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Missed logging a $20 change given out at 2pm" data-testid="cash-close-note" />
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white" onClick={submit} disabled={busy} data-testid="cash-close-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Close day
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function AdjustmentDialog({ source, onDone, onClose }) {
    const [amount, setAmount] = useState("");
    const [direction, setDirection] = useState("cash_out");
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    async function submit() {
        const v = parseFloat(amount);
        if (!(v > 0)) { toast.error("Enter a positive amount"); return; }
        if (!reason.trim()) { toast.error("Adjustment reason is required"); return; }
        setBusy(true);
        try {
            await api.post(`/cash-register/entries/${source.id}/adjust`, {
                direction, amount: v, reason: reason.trim(),
            });
            toast.success("Adjustment posted");
            onDone();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    }
    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="cash-adjust-dialog">
                <DialogHeader>
                    <DialogTitle>Post adjustment</DialogTitle>
                    <DialogDescription>
                        Adjusting <span className="font-mono">{source.reference}</span> — {source.description}. History stays intact; this posts a new adjusting entry.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-2">
                    <button
                        onClick={() => setDirection("cash_in")}
                        className={`rounded-md border px-3 py-2 text-sm ${direction === "cash_in" ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-[#E4E4E7] text-[#5E636E]"}`}
                        data-testid="cash-adjust-in"
                    >
                        <Plus className="inline h-3.5 w-3.5" /> Add cash
                    </button>
                    <button
                        onClick={() => setDirection("cash_out")}
                        className={`rounded-md border px-3 py-2 text-sm ${direction === "cash_out" ? "border-rose-600 bg-rose-50 text-rose-800" : "border-[#E4E4E7] text-[#5E636E]"}`}
                        data-testid="cash-adjust-out"
                    >
                        <Minus className="inline h-3.5 w-3.5" /> Remove cash
                    </button>
                </div>
                <div>
                    <Label className="co-label">Amount (NZD)</Label>
                    <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="cash-adjust-amount" />
                </div>
                <div>
                    <Label className="co-label">Reason (required, kept on record)</Label>
                    <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Miskeyed as $50 instead of $500" data-testid="cash-adjust-reason" />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white" onClick={submit} disabled={busy} data-testid="cash-adjust-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Post adjustment
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function ExportDialog({ clientId, onClose }) {
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [busy, setBusy] = useState(false);
    async function download() {
        setBusy(true);
        try {
            const params = { client_id: clientId };
            if (from) params.date_from = from;
            if (to) params.date_to = to;
            const res = await api.get("/cash-register/export", { params, responseType: "blob" });
            const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const cd = res.headers["content-disposition"] || "";
            const match = cd.match(/filename="([^"]+)"/);
            a.download = match ? match[1] : "cash_ledger.xlsx";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Export downloaded");
            onClose();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    }
    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="cash-export-dialog">
                <DialogHeader>
                    <DialogTitle>Export cash ledger</DialogTitle>
                    <DialogDescription>Downloads an Excel spreadsheet matching your accountant&apos;s ledger layout. Leave dates blank to export everything.</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-2">
                    <div><Label className="co-label">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="cash-export-from" /></div>
                    <div><Label className="co-label">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="cash-export-to" /></div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white" onClick={download} disabled={busy} data-testid="cash-export-download">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Download Excel
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
